///
/// EarthBrowser
///
/// Created by Matt Giger on 11/14/2023
/// Copyright (c) 2023 Geodata Labs
///

import { mix } from "./math"
import { type matrix } from "./matrix"
import { type ray } from "./ray"
import { rect } from "./rect"
import { sphere } from "./sphere"
import { vec3 } from "./vector"

/**
 * Represents a 3D cube or bounding box.
 */
export class cube {
	/** Minimum x-coordinate of the cube */
	public minx: number = Number.POSITIVE_INFINITY
	/** Minimum y-coordinate of the cube */
	public miny: number = Number.POSITIVE_INFINITY
	/** Minimum z-coordinate of the cube */
	public minz: number = Number.POSITIVE_INFINITY
	/** Maximum x-coordinate of the cube */
	public maxx: number = Number.NEGATIVE_INFINITY
	/** Maximum y-coordinate of the cube */
	public maxy: number = Number.NEGATIVE_INFINITY
	/** Maximum z-coordinate of the cube */
	public maxz: number = Number.NEGATIVE_INFINITY

	/**
	 * Creates a new cube instance.
	 * @param min - The minimum value for all dimensions. If undefined, the cube is initialized with infinite bounds.
	 * @param max - The maximum value for all dimensions. If undefined, the cube is initialized with infinite bounds.
	 */
	constructor(min: number | undefined = undefined, max: number | undefined = undefined) {
		if(min !== undefined && max !== undefined) {
			this.minx = min
			this.miny = min
			this.minz = min
			this.maxx = max
			this.maxy = max
			this.maxz = max
		}
	}

	/**
	 * Gets the minimum point of the cube.
	 * @returns A vec3 representing the minimum point.
	 */
	public get min(): vec3 {
		return new vec3(this.minx, this.miny, this.minz)
	}

	/**
	 * Gets the maximum point of the cube.
	 * @returns A vec3 representing the maximum point.
	 */
	public get max(): vec3 {
		return new vec3(this.maxx, this.maxy, this.maxz)
	}

	/**
	 * Gets a specific corner of the cube.
	 * @param idx - The index of the corner (0-7).
	 * @returns A vec3 representing the specified corner.
	 */
	public corner(idx: number): vec3 {
		switch(idx) {
			default:
			case 0: return new vec3(this.maxx, this.maxy, this.maxz)
			case 1: return new vec3(this.minx, this.maxy, this.maxz)
			case 2: return new vec3(this.minx, this.miny, this.maxz)
			case 3: return new vec3(this.maxx, this.miny, this.maxz)
			case 4: return new vec3(this.maxx, this.miny, this.minz)
			case 5: return new vec3(this.maxx, this.maxy, this.minz)
			case 6: return new vec3(this.minx, this.maxy, this.minz)
			case 7: return new vec3(this.minx, this.miny, this.minz)
		}
	}

	/**
	 * Checks if the cube has finite bounds.
	 * @returns True if all bounds are finite, false otherwise.
	 */
	public get finite(): boolean {
		return Number.isFinite(this.minx) && Number.isFinite(this.miny) && Number.isFinite(this.minz) &&
			   Number.isFinite(this.maxx) && Number.isFinite(this.maxy) && Number.isFinite(this.maxz)
	}

	/**
	 * Gets the 2D rectangle projection of the cube.
	 * @returns A rect representing the 2D projection.
	 */
	public get rect(): rect {
		return new rect(this.minx, this.miny, this.maxx - this.minx, this.maxy - this.miny)
	}

	/**
	 * Applies a matrix to the cube.
	 * @param matrix - The matrix to apply.
	 */
	public apply(m: matrix): void {
		const min = m.multVec3(new vec3(this.minx, this.miny, this.minz), 1)
		const max = m.multVec3(new vec3(this.maxx, this.maxy, this.maxz), 1)
		this.minx = Math.min(min.x, max.x)
		this.miny = Math.min(min.y, max.y)
		this.minz = Math.min(min.z, max.z)
		this.maxx = Math.max(min.x, max.x)
		this.maxy = Math.max(min.y, max.y)
		this.maxz = Math.max(min.z, max.z)
	}

	/**
	 * Updates the cube bounds to include the given point.
	 * @param x - The x-coordinate of the point.
	 * @param y - The y-coordinate of the point.
	 * @param z - The z-coordinate of the point (default: 0).
	 */
	public update(x: number, y: number, z: number = 0): void {
		if(this.minx > x) { this.minx = x }
		if(this.miny > y) { this.miny = y }
		if(this.minz > z) { this.minz = z }
		if(this.maxx < x) { this.maxx = x }
		if(this.maxy < y) { this.maxy = y }
		if(this.maxz < z) { this.maxz = z }
	}

	/**
	 * Expands the cube to include another cube.
	 * @param box - The cube to include.
	 */
	public add(box: cube): void {
		if(!box.finite) {
			return
		}
		this.update(box.minx, box.miny, box.minz)
		this.update(box.maxx, box.maxy, box.maxz)
	}

	/**
	 * Checks if this cube fully contains another cube.
	 * @param box - The cube to check.
	 * @returns True if this cube contains the other cube, false otherwise.
	 */
	public contains(box: cube): boolean {
		return this.minx <= box.minx && this.miny <= box.miny && this.minz <= box.minz &&
			   this.maxx >= box.maxx && this.maxy >= box.maxy && this.maxz >= box.maxz
	}

	/**
	 * Gets the centroid (center point) of the cube.
	 * @returns A vec3 representing the centroid.
	 */
	public get centroid(): vec3 {
		if(!Number.isFinite(this.minx)) {
			return vec3.zero
		} else {
			return new vec3(
				mix(this.minx, this.maxx, 0.5),
				mix(this.miny, this.maxy, 0.5),
				mix(this.minz, this.maxz, 0.5)
			)
		}
	}

	/**
	 * Gets the radius of the cube.
	 * This is calculated as half the length of the longest side of the cube.
	 * @returns The radius of the cube.
	 */
	public get radius(): number {
		return Math.max(this.maxx - this.minx, this.maxy - this.miny, this.maxz - this.minz) / 2
	}

	/**
	 * Gets the bounding sphere of the cube.
	 * The sphere is centered at the cube's centroid and has a radius
	 * that encompasses the entire cube.
	 *
	 * @returns A sphere object representing the bounding sphere of the cube.
	 */
	public get sphere(): sphere {
		return new sphere(this.radius, this.centroid)
	}

	/**
	 * Calculates the intersection of a ray with the cube.
	 * @param r - The ray to intersect with the cube.
	 * @returns The distance along the ray to the intersection point, or -1 if no intersection.
	 */
	public intersect(r: ray): number {
		const origin = r.origin
		const inverseDirection = r.inverseDirection

		// Calculate intersection distances for each axis
		const t1 = (this.minx - origin.x) * inverseDirection.x
		const t2 = (this.maxx - origin.x) * inverseDirection.x
		const t3 = (this.miny - origin.y) * inverseDirection.y
		const t4 = (this.maxy - origin.y) * inverseDirection.y
		const t5 = (this.minz - origin.z) * inverseDirection.z
		const t6 = (this.maxz - origin.z) * inverseDirection.z

		// Find the maximum of the minimum intersection distances
		const tmin = Math.max(Math.max(Math.min(t1, t2), Math.min(t3, t4)), Math.min(t5, t6))
		// Find the minimum of the maximum intersection distances
		const tmax = Math.min(Math.min(Math.max(t1, t2), Math.max(t3, t4)), Math.max(t5, t6))

		// Check if there's a valid intersection
		if(tmax < 0 || tmin > tmax) {
			return -1
		}

		// Calculate and return the intersection point
		return tmin
	}
}
